import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  DecodeUserFromLatLngAction,
  GetUserCurrentPositionAction,
  ResetRedirectPageAfterSelectedLocationAction,
  SetRedirectPageAfterSelectedLocationAction,
  SetUserLocationAction,
} from './user-location.actions';
import { Geolocation } from '@capacitor/geolocation';
import { MapGeocoder } from '@angular/google-maps';
import { GeoLocality, GeoPosition } from '../../locations';
import { UpdateUserDefaultLocation } from './user-profile.actions';
import { ensureGoogleMapsLoaded } from '../../../utils/google-maps-loader.util';

export interface UserLocationStateModel {
  /*
   * The three are absent until the person grants the permission and the
   * geocoder answers, which is what the defaults below set. Nothing here is
   * known when the application starts.
   */
  coordinates?: GeoPosition;
  geoResult?: google.maps.GeocoderResult;
  locality?: GeoLocality;
  nextRoute: string;
}

const defaults: UserLocationStateModel = {
  coordinates: undefined,
  geoResult: undefined,
  locality: undefined,
  nextRoute: '',
};

@State<UserLocationStateModel>({
  name: 'userLocation',
  defaults,
})
@Injectable()
export class UserLocationState {
  constructor(private readonly _geocoder: MapGeocoder) {}

  @Action(GetUserCurrentPositionAction)
  async getUserCurrentPositionAction(
    { patchState, dispatch }: StateContext<UserLocationStateModel>,
    _action: GetUserCurrentPositionAction,
  ) {
    const coordinates = await Geolocation.getCurrentPosition();
    patchState({
      coordinates: {
        lat: coordinates.coords.latitude,
        lng: coordinates.coords.longitude,
      },
    });

    return dispatch(new DecodeUserFromLatLngAction());
  }

  @Action(SetUserLocationAction)
  async setUserLocationAction(
    { patchState, dispatch }: StateContext<UserLocationStateModel>,
    { payload }: SetUserLocationAction,
  ) {
    patchState({
      coordinates: payload.position,
      locality: payload.locality,
    });
  }

  @Action(SetRedirectPageAfterSelectedLocationAction)
  async setRedirectPageAfterSelectedLocationAction(
    { patchState }: StateContext<UserLocationStateModel>,
    { route }: SetRedirectPageAfterSelectedLocationAction,
  ) {
    patchState({
      nextRoute: route,
    });
  }

  @Action(ResetRedirectPageAfterSelectedLocationAction)
  async resetRedirectPageAfterSelectedLocationAction({ patchState }: StateContext<UserLocationStateModel>) {
    patchState({
      nextRoute: undefined,
    });
  }

  @Action(DecodeUserFromLatLngAction)
  async decodeUserFromLatLngAction(
    { patchState, getState, dispatch }: StateContext<UserLocationStateModel>,
    _action: DecodeUserFromLatLngAction,
  ) {
    let state = getState();
    if (!state.coordinates) {
      await dispatch(new GetUserCurrentPositionAction());
      state = getState();
    }

    // Angular's MapGeocoder reads `google.maps.Geocoder` in the body of the
    // observable it hands back, with nothing around it. When the Maps script
    // had not loaded, subscribing threw « Can't find variable: google »
    // straight into rxjs and on to Angular's global handler: CAPACITOR-K,
    // four events on 20 September 2026, same session as CAPACITOR-J.
    //
    // Failing here rather than there is what lets the screen answer: the city
    // search screen turns this into « we could not read your location, type
    // your city », and the member has somewhere to go.
    if (!(await ensureGoogleMapsLoaded())) {
      throw new Error('Google Maps is not loaded, a position cannot be turned into a city');
    }

    // Nothing to turn into a city without a position, and this action is
    // reached before the permission is granted as well as after.
    const coordinates = state.coordinates;
    if (!coordinates) {
      return;
    }

    this._geocoder
      .geocode({
        location: {
          lat: coordinates.lat,
          lng: coordinates.lng,
        },
      })
      .subscribe({
        next: ({ results }) => {
          const filtered = results.filter((r) => r.types.includes('locality'));
          let geoResult;
          if (filtered) {
            geoResult = filtered[0];
          } else {
            geoResult = results[0];
          }
          patchState({
            geoResult,
            locality: {
              formattedAddress: geoResult.formatted_address,
              country: geoResult.address_components.find((a) => a.types.includes('country'))?.long_name,
              city: geoResult.address_components.find((a) => a.types.includes('locality'))?.long_name,
              region: geoResult.address_components.find((a) => a.types.includes('administrative_area_level_2'))
                ?.long_name,
            },
          });

          dispatch(new UpdateUserDefaultLocation());
        },
        error: () => {
          // The geocoder answered an error rather than a result. Nothing to
          // patch, and nothing to crash over: the screen still has its search
          // field, and the guard above has already covered the common cause.
        },
      });
  }
}
